import { Button } from 'react-bootstrap';
import { BsPencil } from '@react-icons/all-files/bs/BsPencil';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { useI18n } from "Hooks/useI18n";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { useTenantSettings } from "Hooks/useTenantSettings";
import { AMP_SETTINGS, ConnectorUpsertDto, KIND } from "@ampsec/platform-client";
import { ERROR_MESSAGES, ERROR_VARIANT, INFO_VARIANT } from "Core-utils/constants/constants";
import { ToggleableListItem } from "Components/molecules";
import { BrowserCoachingSubject, CoachingSubject, getBrowserCoachingSubjects } from 'Components/pages/BrowserCoachingPage/data';
import { useApiData } from 'Hooks/useApiData';
import {
    AmplifierConnectorReadinessOperation,
    ensureAmplifierConnectorReadiness,
    getInstalledConnectors,
} from 'Apis/library';
import { extractErrorMessage } from 'Components/pages/ComplianceTrainingPage/utils/helpers';
import { useEarlyAccess } from 'Hooks/useEarlyAccess';

interface BrowserCoachingListViewProps {
  disabled?: boolean;
  onConfigureAiServices?: (e: React.MouseEvent) => void;
}


const API_LIMIT = 250;
const BrowserCoachingListView = ({ disabled = false, onConfigureAiServices }: BrowserCoachingListViewProps) => {
  const { t } = useI18n();
  const [coachingSubjects, setCoachingSubjects] = useState<CoachingSubject[]>([]);
  // State to track the "Optimistic" status of the toggles.
  // We update this immediately on user interaction so the UI feels instant.
  const [activeCoachingSubjects, setActiveCoachingSubjects] = useState<string[]>([]);
  const [savingSubjects, setSavingSubjects] = useState<Set<string>>(new Set());
  const { isDevTier } = useEarlyAccess();

  /** Last server-synced value: whether `BROWSER_COACHING_ACTIVE_SUBJECTS` included AI_SERVICE_SIGNALS. Used to run ensure only on first enable from that baseline. */
  const activeSubjectsHadAiServiceSignalsRef = useRef(false);

  const pendingSettingsChanges = useRef<Map<string, boolean>>(new Map());
  const settingsDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const settingsDebounceDelay = 1250;

  const { tenantSettings, settingsLoading, getAmpSettingValue, updateAmpSetting, refreshTenantSettings } = useTenantSettings();
  const { addToast } = useContext(ToastsContext);
  const { data: _installedConnectors, refresh: refreshConnectors } = useApiData<ConnectorUpsertDto, ConnectorUpsertDto>(
    KIND.CONNECTORS,
    () => getInstalledConnectors({ limit: API_LIMIT }),
    (a) => a,
  );

  const settingsAreLoading = settingsLoading || !tenantSettings || Object.keys(tenantSettings).length === 0;

  useEffect(() => {
    // Guard Clause:
    // If the user is actively toggling switches (pending changes exist),
    // we ignore updates from the backend to prevent the UI from flickering
    // or reverting to the old server state while the user is editing.
    if (settingsAreLoading || pendingSettingsChanges.current.size > 0) {
      return;
    }
    const activeSubjects = getAmpSettingValue(AMP_SETTINGS.tenant.BROWSER_COACHING_ACTIVE_SUBJECTS.id) ?? AMP_SETTINGS.tenant.BROWSER_COACHING_ACTIVE_SUBJECTS.defaultValue;
    activeSubjectsHadAiServiceSignalsRef.current = activeSubjects.includes(BrowserCoachingSubject.AI_SERVICE_SIGNALS);
    setActiveCoachingSubjects(activeSubjects);
  }, [tenantSettings]);

  useEffect(() => {
    if (settingsAreLoading) {
      return;
    }
    let coachingSubjects = getBrowserCoachingSubjects(t, activeCoachingSubjects);
    if (!isDevTier) {
      coachingSubjects = coachingSubjects.filter((subject) => subject.subject !== BrowserCoachingSubject.AI_SERVICE_SIGNALS);
    }
    setCoachingSubjects(coachingSubjects);
  }, [activeCoachingSubjects, settingsAreLoading, isDevTier, t]);

  useEffect(() => {
    return () => {
      if (settingsDebounceTimer.current) {
        clearTimeout(settingsDebounceTimer.current);
      }
    };
  }, []);

  const commitPendingSettingsChanges = async (): Promise<void> => {
    // Batch Save:
    // We take the final state of all toggles and send a single API request.
    const currentActiveSubjects = new Set(activeCoachingSubjects);
    pendingSettingsChanges.current.forEach((enabled, subject) => {
      if (enabled) {
        currentActiveSubjects.add(subject);
      } else {
        currentActiveSubjects.delete(subject);
      }
    });
    const finalSubjects = Array.from(currentActiveSubjects);
    const aiSignalsToggled = pendingSettingsChanges.current.has(BrowserCoachingSubject.AI_SERVICE_SIGNALS);
    const aiSignalsEnabled = finalSubjects.includes(BrowserCoachingSubject.AI_SERVICE_SIGNALS);

    try {
      await updateAmpSetting(AMP_SETTINGS.tenant.BROWSER_COACHING_ACTIVE_SUBJECTS.id, finalSubjects, true);

      if (aiSignalsToggled) {
        if (aiSignalsEnabled) {
          const isFirstEnableFromActiveSubjects =
            !activeSubjectsHadAiServiceSignalsRef.current;
          if (isFirstEnableFromActiveSubjects) {
            await ensureAmplifierConnectorReadiness(AmplifierConnectorReadinessOperation.AMPLIFIER_AI_SIGNALS);
            await refreshConnectors();
          }
        }
        await updateAmpSetting(AMP_SETTINGS.tenant.BROWSER_AI_SERVICE_SIGNALS_ENABLED.id, aiSignalsEnabled, true);
      }

      pendingSettingsChanges.current.clear();
      refreshTenantSettings();  // refresh the tenant settings to get the latest data
      addToast({
        body: t('BROWSER_COACHING_UPDATED'),
        severity: INFO_VARIANT,
      });
    } catch (error: unknown) {
        addToast({
            body: extractErrorMessage(error, ERROR_MESSAGES.CONNECTOR_SETUP),
            severity: ERROR_VARIANT,
        });
    } finally {
        setSavingSubjects(new Set());
    }
  };

  const handleToggleSubject = (subject: string) => async (ev: ChangeEvent<HTMLInputElement>) => {
    const enabled = ev.target.checked;
    setSavingSubjects((prev) => new Set(prev).add(subject));

    // Optimistic Update:
    // Update the local UI state immediately without waiting for the API.
    setActiveCoachingSubjects((prev) => {
      if (enabled) {
        return [...prev, subject];
      } else {
        return prev.filter((s) => s !== subject);
      }
    });

    // Debouncing:
    // Track the change but delay the API call.
    // If the user clicks again within 1.25s, the previous timer is cleared.
    pendingSettingsChanges.current.set(subject, enabled);
    if (settingsDebounceTimer.current) {
      clearTimeout(settingsDebounceTimer.current);
    }
    settingsDebounceTimer.current = setTimeout(async () => {
      await commitPendingSettingsChanges();
    }, settingsDebounceDelay);
  };

  return (
    <div className="d-flex flex-column gap-3">
      {coachingSubjects.map((subject) => {
        const isLoading = savingSubjects.has(subject.subject);
        const isAiServiceCoaching = subject.subject === BrowserCoachingSubject.AI_SERVICE_COACHING;

        return (
          <ToggleableListItem
            key={`coaching-subject-${subject.subject}`}
            id={subject.subject}
            title={subject.name}
            description={subject.description}
            checked={subject.enabled}
            disabled={isLoading || disabled}
            loading={isLoading}
            onChange={handleToggleSubject(subject.subject)}
            titleAccessory={
              isAiServiceCoaching && onConfigureAiServices ? (
                <Button
                  variant="link"
                  className="p-0 text-decoration-none text-medium-emphasis d-flex align-items-center"
                  style={{ lineHeight: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfigureAiServices && onConfigureAiServices(e);
                  }}
                  disabled={disabled}
                  aria-label="Configure AI Services"
                >
                  <BsPencil size={14} />
                </Button>
              ) : undefined
            }
          />
        );
      })}
    </div>
  );
};

export default BrowserCoachingListView;
